import { useEffect, useMemo, useState } from "react";
import type * as React from "react";
import {
  company,
  ecosystemGroups,
  faqs,
  metrics,
  newsroomPosts,
  partners,
  portfolio,
  programPlans,
  processSteps,
  supportCategories,
  team,
  techPartners,
  thesisPillars
} from "./content/site";

type RouteKey =
  | "home"
  | "thesis"
  | "programs"
  | "ecosystem"
  | "partners"
  | "team"
  | "process"
  | "founder-help"
  | "apply"
  | "newsroom"
  | "faq"
  | "legal";

const navItems: Array<{ href: string; label: string }> = [
  { href: "/programs", label: "Programs" },
  { href: "/ecosystem", label: "Ecosystem" },
  { href: "/partners", label: "Partners" },
  { href: "/founder-help", label: "Founder Help" },
  { href: "/apply", label: "Apply" }
];

const routeMap: Record<string, RouteKey> = {
  "/": "home",
  "/thesis": "thesis",
  "/programs": "programs",
  "/portfolio": "ecosystem",
  "/ecosystem": "ecosystem",
  "/partners": "partners",
  "/team": "team",
  "/process": "process",
  "/founder-help": "founder-help",
  "/apply": "apply",
  "/newsroom": "newsroom",
  "/faq": "faq",
  "/legal": "legal"
};

const APPLICATION_API_URL = "https://api.odraventure.com/api/applications";

function normalizePath(pathname: string) {
  const clean = pathname.replace(/\/+$/, "");
  return clean || "/";
}

function getRoute(pathname: string): RouteKey {
  return routeMap[normalizePath(pathname)] ?? "home";
}

function usePathname() {
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const syncPath = () => setPathname(normalizePath(window.location.pathname));
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  return pathname;
}

function AppLink({
  href,
  className,
  children,
  ariaLabel
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  ariaLabel?: string;
}) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (href.startsWith("/") && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      window.history.pushState({}, "", href);
      window.dispatchEvent(new PopStateEvent("popstate"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <a href={href} className={className} aria-label={ariaLabel} onClick={handleClick}>
      {children}
    </a>
  );
}

function Header({ activePath }: { activePath: string }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isActiveNavItem = (href: string) => activePath === href || (href === "/ecosystem" && activePath === "/portfolio");

  useEffect(() => {
    setMobileOpen(false);
  }, [activePath]);

  return (
    <header className="site-header">
      <AppLink href="/" className="brand" ariaLabel="RedQueen Fund home">
        <span className="brand-logo-wrap">
          <img src={company.logo} alt="Regina Purpurea Fundus logo" />
        </span>
      </AppLink>

      <nav className="nav" aria-label="Main navigation">
        {navItems.map((item) => (
          <AppLink
            key={item.href}
            href={item.href}
            className={isActiveNavItem(item.href) ? "active" : undefined}
          >
            {item.label}
          </AppLink>
        ))}
      </nav>

      <button className="mobile-toggle" type="button" onClick={() => setMobileOpen((open) => !open)}>
        <span>{mobileOpen ? "Close" : "Menu"}</span>
      </button>

      {mobileOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {navItems.map((item) => (
            <AppLink key={item.href} href={item.href}>
              {item.label}
            </AppLink>
          ))}
          <AppLink href="/legal">Legal</AppLink>
        </nav>
      )}
    </header>
  );
}

function PageHero({
  kicker,
  title,
  body,
  actions
}: {
  kicker: string;
  title: string;
  body: string;
  actions?: React.ReactNode;
}) {
  return (
    <section className="page-hero section-shell">
      <p className="eyebrow">{kicker}</p>
      <h1>{title}</h1>
      <p>{body}</p>
      {actions && <div className="hero-actions">{actions}</div>}
    </section>
  );
}

function Metrics() {
  return (
    <section className="metrics-band" aria-label="Network metrics">
      {metrics.map((metric) => (
        <div key={metric.label}>
          <strong>{metric.value}</strong>
          <span>{metric.label}</span>
        </div>
      ))}
    </section>
  );
}

function ReputationStrip() {
  return (
    <section className="reputation-strip">
      <div className="section-shell reputation-grid">
        <div>
          <p className="eyebrow">Institutional record</p>
          <h2>Registered in 2004, active as the foundation layer behind a shared founder consortium.</h2>
        </div>
        <p>
          RedQueen Fund presents the public venture identity of {company.legalName}. The site uses
          carefully scoped public language: long-standing foundation history, partner-led execution,
          and $200M+ aggregate platform experience without publishing confidential project material.
        </p>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <section id="top" className="hero section-shell">
        <div className="hero-copy">
          <p className="eyebrow">{company.tagline}</p>
          <h1>Twenty-year institutional history for Europe&apos;s next serious founders.</h1>
          <p className="hero-lede">
            RedQueen Fund is the English operating identity of Fundacja Regina Purpurea Fundus,
            registered in 2004 and working with Odra Venture to connect founders with capital,
            technology grants, strategic partners, and operating help.
          </p>
          <div className="hero-actions">
            <AppLink className="button button-primary" href="/apply">
              Apply as a founder
            </AppLink>
            <AppLink className="button button-secondary" href="/programs">
              View programs
            </AppLink>
          </div>
        </div>

        <div className="hero-ledger" aria-label="RedQueen Fund operating model">
          <div className="ledger-topline">
            <span>RQF / since 2004</span>
            <span>Institutional foundation arm</span>
          </div>
          <div className="ledger-statement">
            <span>{company.legalName}</span>
            <strong>$200M+ aggregate platform experience across technology, infrastructure, and private markets.</strong>
          </div>
          <div className="ledger-grid">
            {["Capital", "Grants", "Pilots", "Syndicate", "No cash fee", "Up to $2M"].map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </section>

      <Metrics />
      <ReputationStrip />
      <ThesisPreview />
      <ProgramPreview />
      <UnifiedEcosystemPreview />
      <PartnerPreview />
      <TeamPreview />
      <NewsPreview />
      <FounderHelpPreview />
      <CTA />
    </>
  );
}

function ThesisPreview() {
  return (
    <section className="section-shell thesis">
      <div className="section-kicker">Investment thesis</div>
      <div className="section-heading">
        <h2>We invest where the next European systems layer is being written.</h2>
        <p>
          RedQueen uses an institutional, network-led approach: concentrated attention,
          real partner access, and a bias toward companies that can sell into serious markets.
        </p>
      </div>
      <div className="thesis-grid">
        {thesisPillars.slice(0, 3).map((pillar) => (
          <article key={pillar.index} className="thesis-card">
            <span>{pillar.index}</span>
            <h3>{pillar.title}</h3>
            <p>{pillar.body}</p>
          </article>
        ))}
      </div>
      <div className="section-action">
        <AppLink className="button button-secondary" href="/thesis">
          Read the full thesis
        </AppLink>
      </div>
    </section>
  );
}

function ProgramMatrix({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "program-grid compact-program-grid" : "program-grid"}>
      {programPlans.map((plan) => (
        <article key={plan.stage} className="program-card">
          <div className="program-card-top">
            <span>{plan.stage}</span>
            <strong>{plan.equity}</strong>
          </div>
          <h3>{plan.capacity}</h3>
          <p>{plan.bestFor}</p>
          <ul>
            {plan.support.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function ProgramPreview() {
  return (
    <section className="program-band">
      <div className="section-shell">
        <div className="section-heading">
          <div>
            <p className="section-kicker">Accelerator programs</p>
            <h2>Three founder tracks, clear equity, no cash program fee.</h2>
          </div>
          <p>
            It is free to apply and there is no cash program fee. If selected, founders receive
            stage-specific capital support, free partner-provided tools, and technology grants while
            equity terms remain transparent.
          </p>
        </div>
        <ProgramMatrix compact />
        <div className="section-action">
          <AppLink className="button button-secondary" href="/programs">
            Compare all programs
          </AppLink>
        </div>
      </div>
    </section>
  );
}

function UnifiedEcosystemPreview() {
  const previewCompanies = [
    ...portfolio.slice(0, 5),
    portfolio.find((companyItem) => companyItem.name === "Adlytica")
  ].filter((companyItem): companyItem is typeof portfolio[number] => Boolean(companyItem));
  const previewExperience = ecosystemGroups.map((group) => group.companies[0]).filter(Boolean);

  return (
    <section className="section-shell portfolio-section unified-ecosystem-preview">
      <div className="section-kicker">Unified ecosystem</div>
      <div className="section-heading compact">
        <h2>One company network for portfolio startups, operating relationships, and partner access.</h2>
        <p>
          Portfolio companies now sit inside the ecosystem page, followed by carefully scoped VC,
          PE, NGO, and controlled venture exposure. No duplicated navigation, no overclaiming.
        </p>
      </div>
      <PortfolioGrid items={previewCompanies} />
      <div className="ecosystem-mini-grid ecosystem-mini-inline">
        {previewExperience.map((item) => (
          <article key={item.name}>
            <span>{item.profile}</span>
            <h3>{item.name}</h3>
            <p>{item.sector}</p>
          </article>
        ))}
      </div>
      <div className="section-action">
        <AppLink className="button button-secondary" href="/ecosystem">
          Explore the ecosystem
        </AppLink>
      </div>
    </section>
  );
}

function PortfolioGrid({ items }: { items: typeof portfolio }) {
  return (
    <div className="portfolio-grid">
      {items.map((companyItem) => (
        <article key={companyItem.name} className="portfolio-card">
          <div className="portfolio-logo">
            <img src={companyItem.logo} alt={`${companyItem.name} logo`} />
          </div>
          <span>{companyItem.sector}</span>
          <h3>{companyItem.name}</h3>
          <p>{companyItem.description}</p>
          <small>{companyItem.stage}</small>
        </article>
      ))}
    </div>
  );
}

function PartnerPreview() {
  return (
    <section className="section-shell partners-section">
      <div className="section-kicker">Consortium Partners</div>
      <div className="section-heading">
        <h2>A syndicate architecture for founders who need leverage fast.</h2>
        <p>
          RedQueen uses the same high-trust partner network as Odra Venture, connecting
          founders with venture funds, accelerators, technology partners, and strategic operators.
        </p>
      </div>
      <PartnerList items={partners.slice(0, 6)} />
      <div className="section-action">
        <AppLink className="button button-secondary" href="/partners">
          See every partner
        </AppLink>
      </div>
    </section>
  );
}

function PartnerList({ items }: { items: typeof partners }) {
  return (
    <div className="partner-list">
      {items.map((partner) => {
        const content = (
          <>
            <div>
              <span>{partner.type}</span>
              <h3>{partner.name}</h3>
            </div>
            <p>{partner.description}</p>
            <dl>
              <div>
                <dt>Capital</dt>
                <dd>{partner.capital}</dd>
              </div>
              <div>
                <dt>Focus</dt>
                <dd>{partner.focus}</dd>
              </div>
              <div>
                <dt>Base</dt>
                <dd>{partner.location}</dd>
              </div>
            </dl>
          </>
        );

        return partner.website ? (
          <a key={partner.name} href={partner.website} className="partner-row" target="_blank" rel="noreferrer">
            {content}
          </a>
        ) : (
          <div key={partner.name} className="partner-row">
            {content}
          </div>
        );
      })}
    </div>
  );
}

function NewsPreview() {
  return (
    <section className="section-shell newsroom-section">
      <div className="section-kicker">Newsroom</div>
      <div className="news-grid">
        {newsroomPosts.slice(0, 3).map((item) => (
          <article key={item.slug} className="news-card">
            <span>{item.category}</span>
            <h3>{item.title}</h3>
            <p>{item.excerpt}</p>
          </article>
        ))}
      </div>
      <div className="section-action">
        <AppLink className="button button-secondary" href="/newsroom">
          View funding notes
        </AppLink>
      </div>
    </section>
  );
}

function TeamPreview() {
  return (
    <section className="section-shell team-section">
      <div className="section-kicker">Team</div>
      <div className="section-heading compact">
        <h2>Investment judgment, founder empathy, and operating context.</h2>
        <p>
          The committee combines capital markets, AI infrastructure, e-commerce,
          venture building, and international market access.
        </p>
      </div>
      <div className="team-grid">
        {team.slice(0, 4).map((member) => (
          <article key={member.name} className="team-card">
            <div className="portrait" aria-hidden={!member.image}>
              {member.image ? <img src={member.image} alt={member.name} /> : <span>{member.name.split(" ").map((n) => n[0]).join("")}</span>}
            </div>
            <div>
              <span>{member.role}</span>
              <h3>{member.name}</h3>
              <p className="member-company">{member.company}</p>
              <p>{member.bio}</p>
              <small>{member.focus}</small>
            </div>
          </article>
        ))}
      </div>
      <div className="section-action">
        <AppLink className="button button-secondary" href="/team">
          Meet the full team
        </AppLink>
      </div>
    </section>
  );
}

function FounderHelpPreview() {
  return (
    <section className="help-band">
      <div className="section-shell help-inner">
        <div>
          <p className="eyebrow">Founder help center</p>
          <h2>Practical support before, during, and after financing.</h2>
        </div>
        <div className="help-stack">
          {["Fundraising narrative", "Technology grants", "Enterprise pilots", "Partner syndication"].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThesisPage() {
  return (
    <>
      <PageHero
        kicker="Investment thesis"
        title="We back systems companies with strategic urgency."
        body="The RedQueen approach is built around concentrated partner access, strong technical markets, and a practical operating plan for each founder."
        actions={<AppLink className="button button-primary" href="/apply">Apply with your company</AppLink>}
      />
      <section className="section-shell split-section">
        <div>
          <p className="section-kicker">Focus areas</p>
          <h2>Where the consortium has the strongest edge.</h2>
        </div>
        <div className="focus-grid">
          {thesisPillars.map((pillar) => (
            <article key={pillar.index} className="focus-card">
              <span>{pillar.index}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="section-shell criteria-section">
        {[
          ["Founder quality", "Speed, clarity, technical depth, and the ability to recruit stronger people."],
          ["Market pressure", "Markets where customers already feel operational, regulatory, cost, or security pain."],
          ["Partner leverage", "Situations where RedQueen, Odra, and the consortium can change the company trajectory."],
          ["Capital path", "Rounds that can be syndicated cleanly and prepared for follow-on investors."]
        ].map(([title, body]) => (
          <article key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </section>
    </>
  );
}

function ProgramsPage() {
  return (
    <>
      <PageHero
        kicker="Programs"
        title="Programs"
        body="A three-stage accelerator model for founders moving from pre-seed validation to institutional Series A readiness."
        actions={<AppLink className="button button-primary" href="/apply">Apply to the accelerator</AppLink>}
      />
      <section className="section-shell terms-panel tight-top">
        <div>
          <p className="eyebrow">Founder terms</p>
          <h2>Free to apply. No cash program fee. Equity is published upfront.</h2>
        </div>
        <p>
          RedQueen Fund and Odra Venture coordinate capital, grant access, technology tools, partner
          introductions, and founder support. Program acceptance and investment decisions remain
          case-by-case, but the baseline equity terms are intentionally simple.
        </p>
      </section>
      <section className="section-shell program-section">
        <ProgramMatrix />
      </section>
      <section className="section-shell support-section">
        <div className="section-heading compact">
          <h2>890+ technology grant pathways and founder tooling routes.</h2>
          <p>
            Access is coordinated through the partner ecosystem and depends on eligibility,
            provider terms, and founder stage.
          </p>
        </div>
        <div className="support-grid">
          {supportCategories.map((category) => (
            <article key={category.name}>
              <strong>{category.count}</strong>
              <h3>{category.name}</h3>
              <p>{category.examples}</p>
            </article>
          ))}
        </div>
      </section>
      <FAQSection />
    </>
  );
}

function EcosystemPage() {
  return (
    <>
      <PageHero
        kicker="Ecosystem"
        title="Ecosystem"
        body="A single company and ecosystem page for the RedQueen, Odra, and partner network. Public portfolio companies appear first, followed by broader market exposure and relationship history."
      />
      <section className="section-shell portfolio-section ecosystem-portfolio-section tight-top">
        <div className="section-heading compact">
          <div>
            <p className="section-kicker">Public company network</p>
            <h2>Portfolio startups and active company relationships.</h2>
          </div>
          <p>
            The companies below are the public-facing startup network connected to RedQueen Fund,
            Odra Venture, and shared funding notes across AI, cyber, climate, energy, fintech,
            and software infrastructure.
          </p>
        </div>
        <PortfolioGrid items={portfolio} />
      </section>
      <section className="section-shell ecosystem-disclaimer tight-top">
        <p>
          The wider names below are curated from the Odra/RPF project universe and are presented as
          ecosystem experience, relationship history, market exposure, or controlled initiatives.
          They should not be read as a complete list of direct RedQueen Fund investments.
        </p>
      </section>
      <section className="section-shell ecosystem-section">
        {ecosystemGroups.map((group) => (
          <div key={group.title} className="ecosystem-group">
            <div>
              <p className="section-kicker">{group.title}</p>
              <p>{group.note}</p>
            </div>
            <div className="ecosystem-grid">
              {group.companies.map((item) => (
                <article key={item.name}>
                  <span>{item.profile}</span>
                  <h3>{item.name}</h3>
                  <small>{item.sector}</small>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        ))}
      </section>
    </>
  );
}

function PartnersPage() {
  return (
    <>
      <PageHero
        kicker="Partners"
        title="A consortium built for founders who need multiple kinds of leverage."
        body="Venture funds, accelerators, technology partners, and strategic operators form the support layer around the shared RedQueen and Odra portfolio."
      />
      <section className="grant-marquee" aria-label="Technology grant partners">
        <div className="marquee-track">
          {[...techPartners, ...techPartners].map((partner, index) => (
            <span key={`${partner.name}-${index}`}>
              {partner.name}
              <small>{partner.category}</small>
            </span>
          ))}
        </div>
      </section>
      <section className="section-shell partners-section tight-top">
        <PartnerList items={partners} />
      </section>
    </>
  );
}

function TeamPage() {
  return (
    <>
      <PageHero
        kicker="Team"
        title="Investment judgment, founder empathy, and operating context."
        body="The committee combines capital markets, AI infrastructure, e-commerce, venture building, technology strategy, and international market access."
      />
      <section className="section-shell team-section tight-top">
        <div className="team-grid">
          {team.map((member) => (
            <article key={member.name} className="team-card">
              <div className="portrait" aria-hidden={!member.image}>
                {member.image ? <img src={member.image} alt={member.name} /> : <span>{member.name.split(" ").map((n) => n[0]).join("")}</span>}
              </div>
              <div>
                <span>{member.role}</span>
                <h3>{member.name}</h3>
                <p className="member-company">{member.company}</p>
                <p>{member.bio}</p>
                <small>{member.focus}</small>
                <div className="tag-list">
                  {member.highlights.map((highlight) => (
                    <b key={highlight}>{highlight}</b>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function ProcessPage() {
  return (
    <>
      <PageHero
        kicker="Process"
        title="A clear path from first signal to portfolio support."
        body="The RedQueen intake process mirrors Odra Venture's founder workflow while adding partner review and syndicate design around the opportunity."
      />
      <section className="section-shell process-section">
        {processSteps.map((step) => (
          <article key={step.step} className="process-step">
            <span>{step.step}</span>
            <div>
              <h3>{step.title}</h3>
              <small>{step.duration}</small>
              <p>{step.body}</p>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}

function FounderHelpPage() {
  return (
    <>
      <PageHero
        kicker="Founder help"
        title="A practical help center for founders entering the RedQueen and Odra network."
        body="Use this page to understand fit, prepare materials, and see the kinds of support the consortium can coordinate."
      />
      <section className="section-shell help-page-grid">
        {[
          ["Prepare your round", "Clarify ask size, use of funds, milestone plan, existing investors, and follow-on requirements."],
          ["Prepare your product story", "Write the customer pain, why now, product wedge, traction, competitors, and category ambition."],
          ["Prepare the partner stack", "List cloud needs, security requirements, pilot targets, compliance needs, and tooling gaps."],
          ["Prepare for diligence", "Keep cap table, incorporation documents, product metrics, financials, roadmap, and data room links ready."]
        ].map(([title, body]) => (
          <article key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </section>
      <FAQSection />
    </>
  );
}

function FAQPage() {
  return (
    <>
      <PageHero
        kicker="FAQ"
        title="FAQ"
        body="Founder, program, partner, and legal basics for working with RedQueen Fund and the Odra Venture consortium."
      />
      <FAQSection />
    </>
  );
}

function FAQSection() {
  return (
    <section className="section-shell faq-section">
      <p className="section-kicker">FAQ</p>
      {faqs.map((faq) => (
        <details key={faq.question}>
          <summary>{faq.question}</summary>
          <p>{faq.answer}</p>
        </details>
      ))}
    </section>
  );
}

function ApplyPage() {
  const [form, setForm] = useState({
    founderName: "",
    email: "",
    linkedin: "",
    company: "",
    website: "",
    sector: "",
    stage: "",
    askAmount: "",
    teamSize: "",
    summary: "",
    market: "",
    competitors: "",
    traction: "",
    useOfFunds: "",
    milestones: "",
    programFit: "",
    supportNeeds: "",
    anythingElse: ""
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const update = (field: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (status === "error") {
      setStatus("idle");
      setMessage("");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    const sourceNote = [
      "RedQueen Fund intake. Source: redqueenfund.com.",
      form.programFit ? `Program fit: ${form.programFit}` : "",
      form.supportNeeds ? `Support needs: ${form.supportNeeds}` : "",
      form.market ? `Market/customer: ${form.market}` : "",
      form.competitors ? `Competitors: ${form.competitors}` : "",
      form.milestones ? `Milestones: ${form.milestones}` : "",
      form.anythingElse
    ].filter(Boolean).join("\n\n");

    const payload = {
      companyName: form.company,
      description: form.summary,
      pitchSummary: form.summary,
      sector: form.sector,
      stage: form.stage,
      teamSize: form.teamSize,
      askAmount: form.askAmount,
      useOfFunds: form.useOfFunds,
      traction: form.traction,
      founder: {
        name: form.founderName,
        email: form.email,
        linkedin: form.linkedin
      },
      founderName: form.founderName,
      firstName: form.founderName.split(" ")[0] || form.founderName,
      lastName: form.founderName.split(" ").slice(1).join(" "),
      founderEmail: form.email,
      founderLinkedIn: form.linkedin,
      website: form.website,
      oneLiner: form.summary,
      whatBuilding: form.summary,
      previousFunding: "",
      previousFundingDetails: "",
      pitchDeckUrl: form.website,
      anythingElse: sourceNote
    };

    try {
      const response = await fetch(APPLICATION_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Submission failed");
      }
      setStatus("success");
      setMessage("Application received. The RedQueen and Odra teams will review your company and route the right partner conversation.");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Submission failed");
    }
  };

  return (
    <>
      <PageHero
        kicker="Founder intake"
        title="Apply to RedQueen Fund."
        body="Free to apply, no cash program fee. Selected founders enter the shared RedQueen and Odra review process for capital, grants, and partner access."
      />
      <section className="section-shell apply-layout">
        <form className="application-panel application-flow" onSubmit={handleSubmit}>
          <div className="form-intro">
            <span>RedQueen founder application</span>
            <p>
              Built for a fast first read: who you are, what you are building, why the timing matters,
              and which RedQueen/Odra support should be routed first.
            </p>
          </div>

          <section className="form-section">
            <div className="form-section-header">
              <span>01</span>
              <div>
                <h2 id="founder-profile-heading">Founder profile</h2>
                <p>Give us the person behind the company and the fastest way to follow up.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                Founder name
                <input name="founderName" required placeholder="Jane Smith" value={form.founderName} onChange={(event) => update("founderName", event.target.value)} />
              </label>
              <label>
                Email
                <input name="email" type="email" required placeholder="jane@company.com" value={form.email} onChange={(event) => update("email", event.target.value)} />
              </label>
              <label className="field-wide">
                LinkedIn
                <input name="linkedin" placeholder="https://linkedin.com/in/..." value={form.linkedin} onChange={(event) => update("linkedin", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <span>02</span>
              <div>
                <h2 id="company-basics-heading">Company basics</h2>
                <p>Keep it crisp: company, market, stage, and the link a reviewer should open first.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                Company
                <input name="company" required placeholder="Company name" value={form.company} onChange={(event) => update("company", event.target.value)} />
              </label>
              <label>
                Website or deck link
                <input name="website" placeholder="https://..." value={form.website} onChange={(event) => update("website", event.target.value)} />
              </label>
              <label>
                Sector
                <input name="sector" required placeholder="AI infrastructure, climate, cyber..." value={form.sector} onChange={(event) => update("sector", event.target.value)} />
              </label>
              <label>
                Team size
                <input name="teamSize" placeholder="4 full-time, 2 contractors..." value={form.teamSize} onChange={(event) => update("teamSize", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <span>03</span>
              <div>
                <h2 id="product-thesis-heading">Product and thesis</h2>
                <p>YC-style clarity: problem, customer, wedge, timing, and competitive edge.</p>
              </div>
            </div>
            <div className="form-grid">
              <label className="field-wide">
                What are you building?
                <textarea name="summary" required rows={5} placeholder="One clear paragraph about the product, customer, traction, and why now." value={form.summary} onChange={(event) => update("summary", event.target.value)} />
              </label>
              <label>
                Market and customer
                <textarea name="market" rows={4} placeholder="Who urgently needs this, and how do they buy?" value={form.market} onChange={(event) => update("market", event.target.value)} />
              </label>
              <label>
                Competitors or substitutes
                <textarea name="competitors" rows={4} placeholder="Who else solves this today, and why do you win?" value={form.competitors} onChange={(event) => update("competitors", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <span>04</span>
              <div>
                <h2 id="traction-heading">Traction and proof</h2>
                <p>Show the signal: revenue, pilots, users, technical proof, partners, or customer pull.</p>
              </div>
            </div>
            <div className="form-grid">
              <label className="field-wide">
                Traction
                <textarea name="traction" rows={4} placeholder="Revenue, pilots, customers, users, technical proof, or partnerships." value={form.traction} onChange={(event) => update("traction", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <span>05</span>
              <div>
                <h2 id="funding-heading">Funding ask</h2>
                <p>Tell us which program track fits and what the capital unlocks.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                Stage
                <select name="stage" required value={form.stage} onChange={(event) => update("stage", event.target.value)}>
                  <option value="">Select stage</option>
                  <option value="pre-seed">Pre-seed</option>
                  <option value="seed">Seed</option>
                  <option value="series-a">Series A</option>
                </select>
              </label>
              <label>
                Ask amount
                <input name="askAmount" required placeholder="$200K, $500K, up to $2M..." value={form.askAmount} onChange={(event) => update("askAmount", event.target.value)} />
              </label>
              <label>
                Use of funds
                <textarea name="useOfFunds" required rows={4} placeholder="How will this capital and partner support change the next 12 months?" value={form.useOfFunds} onChange={(event) => update("useOfFunds", event.target.value)} />
              </label>
              <label>
                Next milestones
                <textarea name="milestones" rows={4} placeholder="What should be true 6-12 months after selection?" value={form.milestones} onChange={(event) => update("milestones", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section">
            <div className="form-section-header">
              <span>06</span>
              <div>
                <h2 id="program-fit-heading">Program fit</h2>
                <p>Help us route the application to capital, grants, pilots, hiring, or syndication support.</p>
              </div>
            </div>
            <div className="form-grid">
              <label>
                Why RedQueen and Odra?
                <textarea name="programFit" rows={4} placeholder="Why this network, and why now?" value={form.programFit} onChange={(event) => update("programFit", event.target.value)} />
              </label>
              <label>
                Support needs
                <textarea name="supportNeeds" rows={4} placeholder="Cloud grants, pilots, hiring, follow-on investors, enterprise access..." value={form.supportNeeds} onChange={(event) => update("supportNeeds", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="form-section final-review">
            <div className="form-section-header">
              <span>07</span>
              <div>
                <h2 id="final-review-heading">Final review</h2>
                <p>Optional context and confirmation before the application enters the shared intake.</p>
              </div>
            </div>
            <label>
              Anything else
              <textarea name="anythingElse" rows={3} placeholder="Anything that helps route this to the right RedQueen/Odra partner." value={form.anythingElse} onChange={(event) => update("anythingElse", event.target.value)} />
            </label>
            <div className="review-note">
              <strong>No cash program fee.</strong>
              <span>Applications are reviewed case by case. Equity terms apply only if selected and agreed.</span>
            </div>
          </section>

          <div className="form-submit-row">
            <button className="button button-primary" type="submit" disabled={status === "loading"}>
              {status === "loading" ? "Submitting..." : "Submit application"}
            </button>
            {message && (
              <p className={status === "error" ? "form-error" : "form-success"}>
                {message}
              </p>
            )}
          </div>
        </form>
        <aside className="apply-notes">
          <span className="aside-kicker">Founder checklist</span>
          <h2>What helps us move faster</h2>
          <ul>
            <li>Clear one-line product description and why now.</li>
            <li>Stage, ask amount, and the exact use of funds.</li>
            <li>Current traction, pilots, customer proof, or technical proof.</li>
            <li>Which support matters most: grants, enterprise pilots, hiring, or syndication.</li>
          </ul>
          <div className="apply-terms">
            {programPlans.map((plan) => (
              <span key={plan.stage}>
                <strong>{plan.stage}</strong>
                <small>{plan.capacity} / {plan.equity}</small>
              </span>
            ))}
          </div>
          <p className="apply-address">
            RedQueen Fund reviews applications from {company.address}. Contact: {company.email}.
          </p>
          <AppLink className="button button-secondary" href="/process">
            See the process
          </AppLink>
        </aside>
      </section>
    </>
  );
}

function NewsroomPage() {
  return (
    <>
      <PageHero
        kicker="Newsroom"
        title="Portfolio funding notes from the shared RedQueen and Odra ecosystem."
        body="A concise record of portfolio work moving forward across the startup network."
      />
      <section className="section-shell newsroom-section tight-top">
        <div className="news-list">
          {newsroomPosts.map((post) => (
            <article key={post.slug} className="news-row">
              <div>
                <span>{post.date} / {post.sector}</span>
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>
              </div>
              <dl>
                <div>
                  <dt>Backing</dt>
                  <dd>{post.backing}</dd>
                </div>
                <div>
                  <dt>Focus</dt>
                  <dd>{post.focus}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

function LegalPage() {
  return (
    <>
      <PageHero
        kicker="Legal"
        title="Company information for Regina Purpurea Fundus."
        body="Public legal identity and website disclaimer for RedQueen Fund."
      />
      <section className="section-shell legal-card">
        <div>
          <h2>{company.legalName}</h2>
          <p>English website name: {company.displayName}</p>
          <p>{company.founded}. Registered office: {company.address}.</p>
          <p>Website contact: {company.email}.</p>
        </div>
        <dl>
          <div>
            <dt>KRS</dt>
            <dd>{company.krs}</dd>
          </div>
          <div>
            <dt>NIP</dt>
            <dd>{company.nip}</dd>
          </div>
          <div>
            <dt>REGON</dt>
            <dd>{company.regon}</dd>
          </div>
        </dl>
        <p>
          This website is an informational presentation of RedQueen Fund and its partner ecosystem.
          Investment, acceleration, grant, and partnership decisions are made case by case.
          Nothing here is investment advice, a public offer, or a commitment to fund any company.
        </p>
      </section>
    </>
  );
}

function CTA() {
  return (
    <section id="contact" className="contact-section">
      <div className="section-shell contact-inner">
        <p className="eyebrow">Founder intake</p>
        <h2>Bring the problem. We will bring the network.</h2>
        <p>
          For founders, co-investors, and strategic partners building around AI, infrastructure,
          cyber, energy, climate, and enterprise software.
        </p>
        <div className="contact-actions">
          <AppLink className="button button-primary" href="/apply">
            Apply as a founder
          </AppLink>
          <a className="button button-secondary dark" href={`mailto:${company.email}?subject=RedQueen%20Fund%20partner%20conversation`}>
            Partner with us
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <strong>{company.displayName}</strong>
        <span>{company.legalName}. Strategic partner of Odra Venture.</span>
      </div>
      <p>
        KRS: {company.krs} / NIP: {company.nip} / REGON: {company.regon}
        <br />
        {company.address} / {company.email}
        <br />
        <AppLink href="/programs">Programs</AppLink> / <AppLink href="/ecosystem">Ecosystem</AppLink> / <AppLink href="/team">Team</AppLink> / <AppLink href="/newsroom">Newsroom</AppLink> / <AppLink href="/faq">FAQ</AppLink> /{" "}
        <AppLink href="/legal">Legal information</AppLink>
      </p>
    </footer>
  );
}

function RouteContent({ route }: { route: RouteKey }) {
  const component = useMemo(() => {
    switch (route) {
      case "thesis":
        return <ThesisPage />;
      case "programs":
        return <ProgramsPage />;
      case "ecosystem":
        return <EcosystemPage />;
      case "partners":
        return <PartnersPage />;
      case "team":
        return <TeamPage />;
      case "process":
        return <ProcessPage />;
      case "founder-help":
        return <FounderHelpPage />;
      case "apply":
        return <ApplyPage />;
      case "newsroom":
        return <NewsroomPage />;
      case "faq":
        return <FAQPage />;
      case "legal":
        return <LegalPage />;
      default:
        return <HomePage />;
    }
  }, [route]);

  return component;
}

export default function App() {
  const pathname = usePathname();
  const route = getRoute(pathname);

  return (
    <>
      <Header activePath={pathname} />
      <main className="site-main">
        <RouteContent route={route} />
      </main>
      <Footer />
    </>
  );
}
